import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * WhatsNewModal — shows the current release notes to a user ONCE per version.
 * Also listens for a global 'bp:whats-new' event so the HelpButton can re-open it.
 */
export default function WhatsNewModal() {
  const nav = useNavigate();
  const [release, setRelease] = useState(null);
  const [open, setOpen] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    http.get('/whats-new').then(({ data }) => {
      setRelease(data.release);
      if (!data.seen) setOpen(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = () => { setForceOpen(true); setOpen(true); };
    window.addEventListener('bp:whats-new', handler);
    return () => window.removeEventListener('bp:whats-new', handler);
  }, []);

  const dismiss = async () => {
    if (!forceOpen && release?.version) {
      try { await http.post('/whats-new/seen', { version: release.version }); } catch { /* no-op */ }
    }
    setForceOpen(false);
    setOpen(false);
  };

  const viewDetails = async () => {
    await dismiss();
    nav('/app/help');
  };

  if (!release || !open) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 flex items-end sm:items-center justify-center p-4 lg:p-8"
        style={{ zIndex: 85, backgroundColor: 'rgba(11,25,44,0.55)' }}
        onClick={dismiss}
        data-testid="whats-new-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg rounded-3xl relative"
          style={{ backgroundColor: '#FFFFFF', boxShadow: '0 40px 100px -20px rgba(11,25,44,0.5)' }}
          data-testid="whats-new-card"
        >
          <button
            data-testid="whats-new-close" onClick={dismiss}
            className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-navy/50 hover:text-navy hover:bg-beige transition-smooth"
            aria-label="Close"
          >
            <X size={16}/>
          </button>

          <div className="p-7 lg:p-10">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
              <Sparkles size={22} className="text-gold" strokeWidth={1.5}/>
            </div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">What’s new</p>
            <h2 className="font-heading text-2xl lg:text-3xl text-navy mt-1 leading-snug">{release.title || 'Fresh in Brotherhood+'}</h2>
            <p className="text-xs text-navy/45 mt-1">v{release.version} · {release.released_at}</p>

            <ul className="mt-5 space-y-2.5">
              {(release.highlights || []).map((h, i) => (
                <li key={i} className="text-sm lg:text-base text-navy/75 leading-relaxed flex gap-2.5" data-testid={`whats-new-item-${i}`}>
                  <span className="text-gold shrink-0 mt-0.5">•</span><span>{h}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex items-center justify-between gap-3">
              <button data-testid="whats-new-dismiss" onClick={dismiss} className="text-sm text-navy/55 hover:text-navy transition-smooth">Dismiss</button>
              <button
                data-testid="whats-new-details" onClick={viewDetails}
                className="inline-flex items-center gap-2 text-sm bg-navy text-white rounded-full px-5 py-2.5 hover:bg-navy-soft transition-smooth"
              >
                View details <ArrowRight size={13}/>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
